import { zodResolver } from "@hookform/resolvers/zod";
import {
  calculateQuote,
  formatBRL,
  type BeneficiaryInput,
  type PricingComponent,
} from "@yatta/domain";
import { Check, ChevronLeft, ChevronRight, HeartPulse, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button, Card, Field } from "../components/ui";
import { useTenantRuntime } from "../config/TenantRuntime";

const schema = z.object({
  offer: z.enum(["FUNERAL_ONLY", "TELEMEDICINE_ONLY", "COMBO"]),
  fullName: z.string().min(3, "Informe o nome completo."),
  cpf: z.string().min(11, "Informe o CPF."),
  birthDate: z.string().min(8, "Informe a data de nascimento."),
  phone: z.string().min(10, "Informe o telefone."),
  email: z.string().email("E-mail inválido."),
  dependents: z.array(
    z.object({
      fullName: z.string().min(3, "Informe o nome."),
      relationship: z.string().min(2, "Informe o parentesco."),
      funeralCovered: z.boolean(),
      telemedicineCovered: z.boolean(),
    }),
  ),
  privacyAccepted: z.boolean().refine(Boolean, "Aceite o aviso de privacidade."),
});

type FormData = z.infer<typeof schema>;

const offerOptions = [
  {
    value: "FUNERAL_ONLY",
    title: "Plano Funerário",
    description: "Assistência e coberturas configuráveis",
    icon: ShieldCheck,
  },
  {
    value: "TELEMEDICINE_ONLY",
    title: "Telemedicina",
    description: "Acesso MobiMedical por vida",
    icon: HeartPulse,
  },
  {
    value: "COMBO",
    title: "Funeral + Saúde",
    description: "Dois produtos com uma jornada comercial",
    icon: Check,
  },
] as const;

const components: PricingComponent[] = [
  {
    id: "00000000-0000-4000-8000-000000000001",
    code: "FUNERAL_ADHESION",
    productCode: "FUNERAL",
    label: "Adesão funerária por vida (demo)",
    type: "INSTALLMENT_PER_PERSON",
    amountCents: 50_000n,
    installments: 10,
    appliesToRoles: ["PRIMARY_HOLDER", "DEPENDENT"],
    required: true,
    selectedByDefault: true,
  },
  {
    id: "00000000-0000-4000-8000-000000000002",
    code: "FUNERAL_MAINTENANCE",
    productCode: "FUNERAL",
    label: "Manutenção mensal demo",
    type: "RECURRING_PERCENTAGE_OF_BASE",
    basisPoints: 100,
    baseComponentCode: "FUNERAL_ADHESION",
    required: true,
    selectedByDefault: true,
  },
  {
    id: "00000000-0000-4000-8000-000000000003",
    code: "TELEMEDICINE_MONTHLY",
    productCode: "TELEMEDICINE",
    label: "Telemedicina por vida (demo)",
    type: "RECURRING_PER_PERSON",
    amountCents: 3_990n,
    appliesToRoles: ["TELEMEDICINE_BENEFICIARY"],
    required: false,
    selectedByDefault: true,
  },
];

export function onboardingEmptyDependentsMessage(count: number) {
  return count === 0
    ? "O titular já participa automaticamente dos produtos escolhidos."
    : undefined;
}

export function OnboardingPage() {
  const { tenant } = useTenantRuntime();
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(false);
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      offer: "COMBO",
      fullName: "",
      cpf: "",
      birthDate: "",
      phone: "",
      email: "",
      dependents: [],
      privacyAccepted: false,
    },
  });
  const dependents = useFieldArray({ control: form.control, name: "dependents" });
  const values = form.watch();

  const quote = useMemo(() => {
    const funeral = values.offer !== "TELEMEDICINE_ONLY";
    const telemedicine = values.offer !== "FUNERAL_ONLY";
    const beneficiaries: BeneficiaryInput[] = [
      {
        personId: "holder",
        roles: ["PRIMARY_HOLDER", ...(telemedicine ? (["TELEMEDICINE_BENEFICIARY"] as const) : [])],
        billable: true,
        funeralCovered: funeral,
        telemedicineCovered: telemedicine,
      },
      ...values.dependents.map((dependent, index) => ({
        personId: `dependent-${index}`,
        roles: [
          "DEPENDENT" as const,
          ...(dependent.telemedicineCovered ? (["TELEMEDICINE_BENEFICIARY"] as const) : []),
        ],
        billable: true,
        funeralCovered: funeral && dependent.funeralCovered,
        telemedicineCovered: telemedicine && dependent.telemedicineCovered,
      })),
    ];

    const selected =
      values.offer === "FUNERAL_ONLY"
        ? ["FUNERAL_ADHESION", "FUNERAL_MAINTENANCE"]
        : values.offer === "TELEMEDICINE_ONLY"
          ? ["TELEMEDICINE_MONTHLY"]
          : ["FUNERAL_ADHESION", "FUNERAL_MAINTENANCE", "TELEMEDICINE_MONTHLY"];

    const activeComponents = components.filter((component) =>
      values.offer === "FUNERAL_ONLY"
        ? component.productCode === "FUNERAL"
        : values.offer === "TELEMEDICINE_ONLY"
          ? component.productCode === "TELEMEDICINE"
          : true,
    );

    return calculateQuote(activeComponents, {
      beneficiaries,
      selectedComponentCodes: selected,
    });
  }, [values.offer, values.dependents]);

  const next = async () => {
    const fieldsByStep: Record<number, Array<keyof FormData>> = {
      1: ["offer"],
      2: ["fullName", "cpf", "birthDate", "phone", "email"],
      3: ["dependents"],
      4: ["privacyAccepted"],
    };
    const valid = await form.trigger(fieldsByStep[step] ?? []);
    if (valid) setStep((current) => Math.min(4, current + 1));
  };

  const submit = form.handleSubmit(async () => {
    setCompleted(true);
    toast.success("Fluxo mock concluído com sucesso.");
  });

  if (completed) {
    return (
      <div className="public-page">
        <Card className="success-card">
          <div className="success-icon">
            <Check size={34} />
          </div>
          <h1>Contratação simulada concluída</h1>
          <p role="status">
            O starter validou cadastro, cálculo e consentimento. No fluxo conectado, seguirão
            assinatura, cobrança Paglog, split e ativações independentes.
          </p>
          <div className="summary-box">
            <span>Primeiro ciclo</span>
            <strong>{formatBRL(quote.grandFirstCycleCents)}</strong>
          </div>
          <Button
            onClick={() => {
              setCompleted(false);
              setStep(1);
              form.reset();
            }}
          >
            Iniciar nova simulação
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-page">
      <header className="onboarding-header">
        <div className="brand-mark">
          <ShieldCheck />
        </div>
        <div>
          <strong>{tenant.branding.shortName}</strong>
          <span>{tenant.branding.productName}</span>
        </div>
      </header>

      <div className="onboarding-layout">
        <div className="demo-warning" role="note">
          Demonstração segura: use dados fictícios. Nenhuma contratação, cobrança, assinatura ou
          ativação clínica real será criada.
        </div>
        <Card className="onboarding-card">
          <div className="stepper" aria-label={`Etapa ${step} de 4`}>
            {[1, 2, 3, 4].map((number) => (
              <div
                aria-current={number === step ? "step" : undefined}
                className={number <= step ? "step active" : "step"}
                key={number}
              >
                <span>{number}</span>
                <small>{["Oferta", "Titular", "Família", "Revisão"][number - 1]}</small>
              </div>
            ))}
          </div>

          <form onSubmit={(event) => void submit(event)}>
            {step === 1 && (
              <div className="form-section">
                <div>
                  <p className="eyebrow">PASSO 1</p>
                  <h1>Escolha a proteção</h1>
                </div>
                <div className="offer-options">
                  {offerOptions.map((option) => {
                    const OfferIcon = option.icon;
                    return (
                      <label
                        className={
                          values.offer === option.value ? "offer-option selected" : "offer-option"
                        }
                        key={option.value}
                      >
                        <input type="radio" value={option.value} {...form.register("offer")} />
                        <OfferIcon />
                        <strong>{option.title}</strong>
                        <span>{option.description}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="form-section">
                <div>
                  <p className="eyebrow">PASSO 2</p>
                  <h1>Dados do titular</h1>
                </div>
                <div className="form-grid">
                  <Field label="Nome completo" error={form.formState.errors.fullName?.message}>
                    <input {...form.register("fullName")} />
                  </Field>
                  <Field label="CPF" error={form.formState.errors.cpf?.message}>
                    <input inputMode="numeric" {...form.register("cpf")} />
                  </Field>
                  <Field
                    label="Data de nascimento"
                    error={form.formState.errors.birthDate?.message}
                  >
                    <input type="date" {...form.register("birthDate")} />
                  </Field>
                  <Field label="WhatsApp" error={form.formState.errors.phone?.message}>
                    <input inputMode="tel" {...form.register("phone")} />
                  </Field>
                  <Field label="E-mail" error={form.formState.errors.email?.message}>
                    <input type="email" {...form.register("email")} />
                  </Field>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="form-section">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">PASSO 3</p>
                    <h1>Família e beneficiários</h1>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      dependents.append({
                        fullName: "",
                        relationship: "",
                        funeralCovered: values.offer !== "TELEMEDICINE_ONLY",
                        telemedicineCovered: values.offer !== "FUNERAL_ONLY",
                      })
                    }
                  >
                    Adicionar pessoa
                  </Button>
                </div>

                {dependents.fields.length === 0 && (
                  <div className="empty-state">
                    <strong>Nenhum dependente incluído</strong>
                    <p>{onboardingEmptyDependentsMessage(dependents.fields.length)}</p>
                  </div>
                )}

                <div className="dependent-list">
                  {dependents.fields.map((field, index) => (
                    <div className="dependent-card" key={field.id}>
                      <div className="form-grid">
                        <Field label="Nome">
                          <input {...form.register(`dependents.${index}.fullName`)} />
                        </Field>
                        <Field label="Parentesco">
                          <input {...form.register(`dependents.${index}.relationship`)} />
                        </Field>
                      </div>
                      <div className="checkbox-row">
                        {values.offer !== "TELEMEDICINE_ONLY" && (
                          <label>
                            <input
                              type="checkbox"
                              {...form.register(`dependents.${index}.funeralCovered`)}
                            />{" "}
                            Funeral
                          </label>
                        )}
                        {values.offer !== "FUNERAL_ONLY" && (
                          <label>
                            <input
                              type="checkbox"
                              {...form.register(`dependents.${index}.telemedicineCovered`)}
                            />{" "}
                            Telemedicina
                          </label>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => dependents.remove(index)}
                        >
                          Remover
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="form-section">
                <div>
                  <p className="eyebrow">PASSO 4</p>
                  <h1>Revise a simulação</h1>
                </div>
                <div className="quote-lines">
                  {quote.lines.map((line) => (
                    <div key={line.componentCode}>
                      <span>
                        {line.label} · {line.quantity}x
                      </span>
                      <strong>{formatBRL(line.totalAmountCents)}</strong>
                    </div>
                  ))}
                </div>
                <div className="quote-total">
                  <span>Primeiro ciclo estimado</span>
                  <strong>{formatBRL(quote.grandFirstCycleCents)}</strong>
                </div>
                <p className="demo-warning">
                  Valores exclusivamente sintéticos para desenvolvimento. A produção ficará
                  bloqueada até a aprovação comercial.
                </p>
                <label className="consent-row">
                  <input
                    type="checkbox"
                    aria-describedby="privacy-notice"
                    {...form.register("privacyAccepted")}
                  />
                  <span>
                    <span id="privacy-notice">
                      Li o aviso de privacidade e autorizo o tratamento necessário para esta
                      simulação.
                    </span>
                  </span>
                </label>
                {form.formState.errors.privacyAccepted && (
                  <span className="field-error">
                    {form.formState.errors.privacyAccepted.message}
                  </span>
                )}
              </div>
            )}

            <div className="wizard-actions">
              <Button
                type="button"
                variant="secondary"
                disabled={step === 1}
                onClick={() => setStep((current) => Math.max(1, current - 1))}
              >
                <ChevronLeft size={17} /> Voltar
              </Button>
              {step < 4 ? (
                <Button type="button" onClick={() => void next()}>
                  Continuar <ChevronRight size={17} />
                </Button>
              ) : (
                <Button type="submit">Concluir simulação</Button>
              )}
            </div>
          </form>
        </Card>

        <aside className="onboarding-summary">
          <span>Estimativa do primeiro ciclo</span>
          <strong>{formatBRL(quote.grandFirstCycleCents)}</strong>
          <small>{values.dependents.length + 1} vida(s) no cadastro</small>
          <hr />
          <p>O valor final dependerá das regras aprovadas para produção.</p>
        </aside>
      </div>
    </div>
  );
}
